// "What these sessions share" (D62 cohort compare, D79): the dimensions over-represented in the
// sessions of an issue against the app's other sessions. A share bar per value, the cohort's
// share against the rest's, never a hue per series (docs/brand.md, Charts).
import { useQuery } from '@tanstack/react-query'
import { issueCohortQuery } from '../api/queries'
import type { CohortComparison } from '../api/types'
import { useI18n } from '../i18n'
import { Section } from './d'
import { ErrorState, Loading } from './states'

type Dimension = CohortComparison['dimensions'][number]['dimension']

const DIMENSION_KEY: Record<Dimension, `cohort.dim.${Dimension}`> = {
  device_model: 'cohort.dim.device_model',
  os_version: 'cohort.dim.os_version',
  release: 'cohort.dim.release',
  country: 'cohort.dim.country',
  feature_flag: 'cohort.dim.feature_flag',
  network_type: 'cohort.dim.network_type',
}

/** The values worth showing: over-represented ones, the biggest difference first, two each. */
export function sharedValues(c: CohortComparison, perDimension = 2) {
  if (!c.comparable) return []
  return c.dimensions
    .map((d) => ({
      dimension: d.dimension,
      values: d.values.filter((v) => v.difference > 0).slice(0, perDimension),
    }))
    .filter((d) => d.values.length > 0)
}

export function CohortPanel({
  projectId,
  issueId,
  appId,
}: {
  projectId: string
  issueId: string
  appId: string
}) {
  const { t, percent } = useI18n()
  const q = useQuery(issueCohortQuery(projectId, issueId, appId))
  const shared = q.data ? sharedValues(q.data) : []
  return (
    <Section
      title={t('cohort.title')}
      meta={q.data ? t('cohort.sub', { sessions: q.data.cohort.sessions }) : undefined}
    >
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} retry={() => q.refetch()} />
      ) : !q.data.comparable ? (
        <p className="empty">
          {q.data.cohort.sessions === 0 ? t('cohort.noSessions') : t('cohort.noRest')}
        </p>
      ) : shared.length === 0 ? (
        <p className="empty">{t('cohort.nothing')}</p>
      ) : (
        <div>
          {shared.flatMap((d) =>
            d.values.map((v) => (
              <div className="share" key={`${d.dimension}-${v.value}`}>
                <span>
                  {t(DIMENSION_KEY[d.dimension])} = <b translate="no">{v.value}</b>
                  <span className="is-vs">
                    {t('cohort.share', {
                      count: v.cohort_sessions,
                      total: q.data.cohort.sessions,
                    })}
                    {' · '}
                    {t('cohort.vs', {
                      cohort: percent(v.cohort_share, 0),
                      rest: percent(v.rest_share, 0),
                    })}
                  </span>
                </span>
                <span className="b" aria-hidden="true">
                  <i style={{ width: `${Math.max(0, Math.min(100, v.cohort_share * 100))}%` }} />
                </span>
                <span>{percent(v.cohort_share, 0)}</span>
              </div>
            )),
          )}
        </div>
      )}
    </Section>
  )
}
